import React, { useMemo } from "react";
import { Badge } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type {
  Reservation,
  ReservationStatus,
} from "../../../../utils/api/reservationsAPI";
import { STATUS_CONFIG } from "../ReservationCard/ReservationCard";
import "./ReservationWeekView.scss";

type Props = {
  reservations: Reservation[];
  weekStart: dayjs.Dayjs;
  onDayClick: (date: dayjs.Dayjs) => void;
  onCardClick: (r: Reservation) => void;
};

const ReservationWeekView: React.FC<Props> = ({
  reservations,
  weekStart,
  onDayClick,
  onCardClick,
}) => {
  const { t } = useTranslation();
  const today = dayjs();

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day")),
    [weekStart]
  );

  // Map date string → reservations
  const byDay = useMemo(() => {
    const map = new Map<string, Reservation[]>();
    for (const r of reservations) {
      const key = dayjs(r.date).format("YYYY-MM-DD");
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    return map;
  }, [reservations]);

  return (
    <div className="rwv">
      {days.map((day) => {
        const key = day.format("YYYY-MM-DD");
        const items = byDay.get(key) || [];
        const isToday = day.isSame(today, "day");
        const totalGuests = items.reduce((s, r) => s + r.numberOfGuests, 0);

        return (
          <div
            key={key}
            className={`rwv-day${isToday ? " rwv-day--today" : ""}${
              items.length === 0 ? " rwv-day--empty" : ""
            }`}
            onClick={() => onDayClick(day)}
          >
            <div className="rwv-day__header">
              <span className="rwv-day__weekday">{day.format("ddd")}</span>
              <span className={`rwv-day__date${isToday ? " rwv-day__date--today" : ""}`}>
                {day.format("D")}
              </span>
            </div>

            {items.length > 0 ? (
              <>
                <div className="rwv-day__summary">
                  <span className="rwv-day__count">{items.length}</span>
                  <span className="rwv-day__guests">{totalGuests} {t("guests")}</span>
                </div>

                <div className="rwv-day__list">
                  {items.slice(0, 4).map((r) => {
                    const cfg = STATUS_CONFIG[r.status];
                    return (
                      <div
                        key={r._id}
                        className="rwv-item"
                        style={{ borderLeftColor: cfg.dot }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onCardClick(r);
                        }}
                      >
                        <span className="rwv-item__time">{r.time}</span>
                        <span className="rwv-item__name">{r.guestName}</span>
                        <span className="rwv-item__guests">
                          {r.numberOfGuests}p
                        </span>
                      </div>
                    );
                  })}
                  {items.length > 4 && (
                    <div className="rwv-day__more">
                      +{items.length - 4} {t("more")}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="rwv-day__no-res">{t("No reservations")}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ReservationWeekView;
